import { gql } from "@apollo/client";
import useOrgSharpConfigContext from "@src/lib/contexts/OrgSharpConfigContext/hooks/useOrgSharpConfigContext";
import { Input } from "@src/lib/ui/Input";
import { cn } from "@src/lib/utils/cn";
import { useToast } from "@src/lib/hooks/useToast";
import { useTracking } from "@src/features/app/hooks/useTracking";
import {
  AddressInput,
  CreateLocationInput,
  GraphqlOperations,
  LicenseType,
  LocationGroupType,
} from "@src/types.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useRouter } from "next/router";
import { FC, useCallback, useMemo, useState } from "react";
import {
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useLocationDialog_LocationQuery,
} from "@src/features/locations/dialogs/LocationDialog.generated";
import useLocationGroups from "@src/features/locations/hooks/useLocationGroups";
import AddressField from "@src/features/locations/AddressField";
import Text from "@src/lib/ui/Text";
import LocationGroupMultiSelectField from "@src/features/locations/ui/LocationGroupMultiSelectField";
import LocationGroupSingleSelectField from "@src/features/locations/ui/LocationGroupSingleSelectField";
import { isNotNullOrUndefined } from "@src/lib/utils/typeguards";
import { getLocationDetailRoute } from "@src/features/users/lib/utils";
import { useLicenseType } from "@src/features/settings/hooks/useLicenseType";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id?: number;
  initialInput?: Partial<CreateLocationInput>;
  navigateToLocationOnSave?: boolean;
  onLocationCreated?: (locationId: number) => void;
};

const LocationDialog: FC<Props> = (props) => {
  const { orgSharpConfig } = useOrgSharpConfigContext();
  const addressRequired = useMemo(() => !!orgSharpConfig, [orgSharpConfig]);
  const router = useRouter();
  const [address, setAddress] = useState<AddressInput | undefined>(
    props.initialInput?.addressInput || undefined,
  );
  const [name, setName] = useState(props.initialInput?.name || "");
  const [locationGroupIds, setLocationGroupIds] = useState<string[]>(
    props.initialInput?.locationGroupIds?.value || [],
  );
  const { locationGroups: allLocationGroups, locationGroupTypes } =
    useLocationGroups();
  const selectedLocationGroups = useMemo(() => {
    return allLocationGroups.filter((x) => locationGroupIds.includes(x.id));
  }, [allLocationGroups, locationGroupIds]);
  const [hasNameValidationErrors, setHasNameValidationErrors] = useState(false);
  const [hasAddressValidationErrors, setHasAddressValidationErrors] =
    useState(false);
  useLocationDialog_LocationQuery({
    skip: !props.id,
    variables: {
      id: props.id || -1,
    },
    onCompleted(data) {
      if (data.Location) {
        setName(data.Location.name);
        if (data.Location.address) {
          setAddress(data.Location.address);
        }
        setLocationGroupIds(
          data.Location.locationGroupMemberships.map(
            (membership) => membership.locationGroup.id,
          ),
        );
      }
    },
  });
  const { addErrorToast } = useToast();
  const { trackEvent } = useTracking();
  const input: CreateLocationInput = useMemo(() => {
    return {
      name,
      addressInput: address,
      locationGroupIds: {
        value: locationGroupIds,
      },
    };
  }, [address, locationGroupIds, name]);
  const close = useCallback(() => {
    props.onOpenChange(false);
  }, [props]);
  const [createLocationMutation, { loading: createLocationLoading }] =
    useCreateLocationMutation({
      variables: {
        input,
      },
      refetchQueries: REFETCH_QUERIES,
      onCompleted: (data) => {
        if (!data.createLocation.success) {
          addErrorToast({
            data,
            callsite: "new_location_dialog",
          });
        } else {
          trackEvent("created_location", {
            id: data.createLocation?.location?.id,
          });

          if (data.createLocation?.location?.id && props.onLocationCreated) {
            props.onLocationCreated(data.createLocation.location.id);
          }

          close();
          if (!props.onLocationCreated) {
            launchConfetti();
          }
          if (
            props.navigateToLocationOnSave &&
            data.createLocation?.location?.id
          ) {
            const id = data.createLocation.location.id;
            router.push(getLocationDetailRoute(id));
          }
        }
      },
      onError: (error) => {
        addErrorToast({
          ...error,
          callsite: "new_location_dialog",
        });
      },
    });
  const [updateLocationMutation, { loading: updateLocationLoading }] =
    useUpdateLocationMutation({
      variables: {
        id: props.id || -1,
        input,
      },
      refetchQueries: REFETCH_QUERIES,
      onCompleted: (data) => {
        if (!data.updateLocation.success) {
          addErrorToast({
            data,
            callsite: "update_location_dialog",
          });
        } else {
          close();
        }
      },
      onError: (error) => {
        addErrorToast({
          ...error,
          callsite: "update_location_dialog",
        });
      },
    });
  const onSave = useCallback(() => {
    const _nameValidationErrors = !name;
    const _addressValidationErrors = addressRequired && !address?.lineOne;
    const hasValidationErrors =
      _nameValidationErrors || _addressValidationErrors;
    if (hasValidationErrors) {
      setHasNameValidationErrors(_nameValidationErrors);
      setHasAddressValidationErrors(_addressValidationErrors);
    } else if (props.id) {
      updateLocationMutation();
    } else {
      createLocationMutation();
    }
  }, [
    address,
    addressRequired,
    createLocationMutation,
    name,
    props.id,
    updateLocationMutation,
  ]);
  const licenseType = useLicenseType();
  const isPerLocation = licenseType === LicenseType.PerLocation;
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className="max-w-[439px] gap-0 p-0"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest?.(".pac-container")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="shrink-0 border-b border-border p-4">
          <DialogTitle>
            {props.id ? "Edit Location" : "New Location"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-1 flex-col gap-[18px] self-stretch p-6">
          {isPerLocation && (
            <div className="rounded-lg bg-blue-2 py-2 pr-4 pl-2">
              <Text variant="compact-title" className="text-blue-11" multiline>
                This location will count as active once you add a user to it.
                You can view the number of location licenses you have in the
                Billing tab.
              </Text>
            </div>
          )}
          <Input
            label="Name"
            value={name}
            onChange={(e) => {
              setHasNameValidationErrors(false);
              setName(e.target.value);
            }}
            data-testid="location-name-input"
            inputClassName={cn(
              "h-10",
              hasNameValidationErrors &&
                "border-red-9 focus-visible:ring-red-9",
            )}
            placeholder="Write name..."
          />
          <AddressFieldInput
            address={address}
            setAddress={(x) => {
              setHasAddressValidationErrors(false);
              setAddress(x);
            }}
            required={addressRequired}
            hasValidationErrors={hasAddressValidationErrors}
          />
          {locationGroupTypes?.map((type) => {
            if (type === LocationGroupType.Region) {
              return (
                <LocationGroupMultiSelectField
                  key={type}
                  type={type}
                  placeholder={"None"}
                  locationGroupIds={selectedLocationGroups
                    .filter((x) => x.type === type)
                    .map((x) => x.id)}
                  setLocationGroupIds={(locationGroupIds) => {
                    setLocationGroupIds([
                      ...selectedLocationGroups
                        .filter((x) => x.type !== type)
                        .map((x) => x.id),
                      ...locationGroupIds,
                    ]);
                  }}
                />
              );
            } else {
              return (
                <LocationGroupSingleSelectField
                  key={type}
                  type={type}
                  locationGroupId={
                    selectedLocationGroups.filter((x) => x.type === type)[0]?.id
                  }
                  placeholder={"None"}
                  setLocationGroupId={(locationGroupId) => {
                    const newVal = [
                      ...selectedLocationGroups
                        .filter((x) => x.type !== type)
                        .map((x) => x.id),
                    ];
                    if (isNotNullOrUndefined(locationGroupId)) {
                      newVal.push(locationGroupId);
                    }
                    setLocationGroupIds(newVal);
                  }}
                />
              );
            }
          })}
        </div>
        <DialogFooter className="border-t border-gray-5-alpha p-3">
          <Button variant="outline" size="lg" onClick={close}>
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={onSave}
            loading={props.id ? updateLocationLoading : createLocationLoading}
          >
            {props.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddressFieldInput: FC<{
  address?: AddressInput;
  setAddress: (x: AddressInput) => void;
  required?: boolean;
  hasValidationErrors?: boolean;
}> = (props) => {
  return (
    <div className="flex flex-col gap-1 self-stretch">
      <div className="flex justify-between self-stretch">
        <Text variant="fine-print-title" className="text-black">
          Address
        </Text>
        <Text variant="fine-print-title" className="text-gray-7">
          {props.required ? "Required" : "Recommended"}
        </Text>
      </div>
      <AddressField
        initialAddress={props.address}
        onAddressChange={props.setAddress}
        textFieldProps={{
          inputClassName: "h-10",
        }}
        hasValidationError={props.hasValidationErrors}
      />
    </div>
  );
};

gql`
  query locationDialog_Location($id: Int!) {
    Location(id: $id) {
      ...LocationDialog_Location
    }
  }
  mutation createLocation($input: CreateLocationInput!) {
    createLocation(input: $input) {
      success
      location {
        ...LocationDialog_LocationWithAddress
      }
    }
  }
  mutation updateLocation($id: Int!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      success
      location {
        ...LocationDialog_LocationWithAddress
      }
    }
  }
  fragment LocationDialog_LocationWithAddress on Location {
    id
    name
    address {
      ...Address
    }
  }
  fragment LocationDialog_Location on Location {
    id
    name
    address {
      ...Address
    }
    locationGroupMemberships {
      id
      locationGroup {
        id
        name
        type
      }
    }
  }
  query LocationDialog_LocationGroup($type: LocationGroupType!) {
    LocationGroups(input: { types: [$type] }) {
      objects {
        id
        name
      }
    }
  }
`;

export default LocationDialog;

const REFETCH_QUERIES = [
  GraphqlOperations.Query.GetLocations,
  GraphqlOperations.Query.LocationsTable_Locations,
  GraphqlOperations.Query.LocationGroupLocationsTable_Locations,
  GraphqlOperations.Query.LocationDetail,
];
